import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Avatar,
  Alert,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const Login: React.FC = () => {
  const { signInWithGoogle } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    // Check for error in URL parameters
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [location]);

  const handleGoogleSignIn = async () => {
    setError(null); // Clear any previous errors
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      setIsSigningIn(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #32CD32 0%, #20B2AA 50%, #87CEEB 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating Logos */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          animation: 'float1 6s ease-in-out infinite',
          '@keyframes float1': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(5deg)' },
          },
        }}
      >
        <Box
          component="img"
          src="/logos/netflix.png" 
          alt="Netflix" 
          sx={{
            width: { xs: '40px', sm: '50px', md: '80px', lg: '100px' },
            height: { xs: '40px', sm: '50px', md: '80px', lg: '100px' },
            opacity: 1, 
            borderRadius: '12px', 
            filter: 'blur(1px)'
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '15%',
          animation: 'float2 8s ease-in-out infinite',
          '@keyframes float2': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-15px) rotate(-3deg)' },
          },
        }}
      >
        <Box
          component="img"
          src="/logos/youtube.png" 
          alt="YouTube" 
          sx={{
            width: { xs: '35px', sm: '45px', md: '70px', lg: '90px' },
            height: { xs: '35px', sm: '45px', md: '70px', lg: '90px' },
            opacity: 1, 
            borderRadius: '12px', 
            filter: 'blur(1px)'
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '15%',
          animation: 'float3 7s ease-in-out infinite',
          '@keyframes float3': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-25px) rotate(2deg)' },
          },
        }}
      >
        <Box
          component="img"
          src="/logos/spotify.png" 
          alt="Spotify" 
          sx={{
            width: { xs: '38px', sm: '48px', md: '75px', lg: '95px' },
            height: { xs: '38px', sm: '48px', md: '75px', lg: '95px' },
            opacity: 1, 
            borderRadius: '12px', 
            filter: 'blur(1px)'
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '10%',
          animation: 'float4 9s ease-in-out infinite',
          '@keyframes float4': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-18px) rotate(-4deg)' },
          },
        }}
      >
        <Box
          component="img"
          src="/logos/disney-hotstar.png" 
          alt="Disney+" 
          sx={{
            width: { xs: '30px', sm: '40px', md: '65px', lg: '85px' },
            height: { xs: '30px', sm: '40px', md: '65px', lg: '85px' },
            opacity: 1, 
            borderRadius: '12px', 
            filter: 'blur(1px)'
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: '90%',
          left: '55%',
          animation: 'float5 10s ease-in-out infinite',
          '@keyframes float5': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-22px) rotate(3deg)' },
          },
        }}
      >
        <Box
          component="img"
          src="/logos/chatgpt.png" 
          alt="ChatGPT" 
          sx={{
            width: { xs: '25px', sm: '35px', md: '60px', lg: '80px' },
            height: { xs: '25px', sm: '35px', md: '60px', lg: '80px' },
            opacity: 1, 
            borderRadius: '12px', 
            filter: 'blur(1px)'
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          animation: 'float6 11s ease-in-out infinite',
          '@keyframes float6': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-16px) rotate(-2deg)' },
          },
        }}
      >
        <Box
          component="img"
          src="/logos/prime-video.png" 
          alt="Prime Video" 
          sx={{
            width: { xs: '32px', sm: '42px', md: '68px', lg: '88px' },
            height: { xs: '32px', sm: '42px', md: '68px', lg: '88px' },
            opacity: 1, 
            borderRadius: '12px', 
            filter: 'blur(1px)'
          }}
        />
      </Box>
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 400,
          opacity: 0.9,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Avatar
          sx={{
            m: 1,
            bgcolor: 'primary.main',
            width: 80,
            height: 80,
            fontSize: '2rem',
          }}
        >
          <Box
            component="img"
            src="/images/SubLime_Mascot.png"
            alt="SubLime Mascot"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Avatar>
        
        <Typography component="h1" variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
          SubLime
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
          Track and manage all your subscriptions in one place
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
          <Typography variant="body2">
            <strong>🔒 Secure Authentication:</strong> Your sign-in is handled securely by Google and Supabase.
          </Typography>
        </Alert>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
            {error}
          </Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleGoogleSignIn}
          disabled={isSigningIn}
          startIcon={
            isSigningIn ? (
              <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 16, height: 16, border: '2px solid #757575', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </Box>
            ) : (
              <img 
                src="https://th.bing.com/th/id/R.0fa3fe04edf6c0202970f2088edea9e7?rik=joOK76LOMJlBPw&riu=http%3a%2f%2fpluspng.com%2fimg-png%2fgoogle-logo-png-open-2000.png&ehk=0PJJlqaIxYmJ9eOIp9mYVPA4KwkGo5Zob552JPltDMw%3d&risl=&pid=ImgRaw&r=0"
                alt="Google"
                style={{
                  width: '20px',
                  height: '20px',
                  objectFit: 'contain'
                }}
              />
            )
          }
          sx={{
            mt: 3,
            mb: 2,
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: '#ffffff',
            color: '#757575',
            border: '1px solid #dadce0',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              border: '1px solid #dadce0',
            },
            '&:disabled': {
              backgroundColor: '#f5f5f5',
              color: '#bdbdbd',
            },
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          {isSigningIn ? 'Signing in...' : 'Sign in with Google'}
        </Button>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login; 